<script lang="ts">
export type ArticleContentsCategory = {
  category_id: number;
  category_name: string;
};
</script>

<script setup lang="ts">
// TODO: 最終的には、kiwi-adminの packages/kiwi-admin/components/organisms/ArticlePreviewModal.vue も当コンポーネントに統一したい
import { computed, ref } from 'vue';
import type { PropType } from 'vue';
import HeadingLv2 from '@shared/app/components/atoms/ArticleContentsHeadingLv2.vue';
import HeadingLv3 from '@shared/app/components/atoms/ArticleContentsHeadingLv3.vue';
import HeadingLv4 from '@shared/app/components/atoms/ArticleContentsHeadingLv4.vue';
import RelationKeywords from '@shared/app/components/atoms/ArticleContentsRelationKeywords.vue';
import Pagination from '@shared/app/components/atoms/ArticleContentsPagination.vue';
import ShareButtonGroup from '@shared/app/components/atoms/ShareButtonGroup.vue';
import FacebookFollowButton from '@shared/app/components/atoms/FacebookFollowButton.vue';
import { useImageUrl } from '@shared/app/composables/useImageUrl';
import type { GetPublicArticleDetailByPermalinkQueryArticleExtended } from '@shared/app/types/kiwiUserServer';
import { formatPublicTs } from '@shared/app/utils/formatPublicTs';
import { useSanitizeHtml } from '@shared/app/composables/useSanitizeHtml';
import { useTransformArticle } from '@shared/app/composables/useTransformArticle';

const contentsRef = ref<HTMLDivElement>();
const contentAnchorRefs = ref<HTMLHeadingElement[]>();

const props = defineProps({
  article: {
    required: true,
    type: Object as PropType<GetPublicArticleDetailByPermalinkQueryArticleExtended>,
  },
  isPr: {
    required: true,
    type: Boolean,
  },
  categories: {
    required: true,
    type: Array as PropType<ArticleContentsCategory[]>,
  },
  currentPage: {
    required: true,
    type: Number,
  },
});

const emit = defineEmits<{
  (e: 'changePage', value: number): void;
  (
    e: 'clickedToc',
    value: {
      page: number;
      pageSection: number;
    },
  ): void;
}>();

const { createArticleImageUrl } = useImageUrl();

function scrollToContents() {
  contentsRef.value?.scrollIntoView({
    behavior: 'smooth',
  });
}

const writerRef = ref<HTMLDivElement>();

function scrollToWriter() {
  if (!writerRef.value) {
    return;
  }

  writerRef.value.scrollIntoView({
    behavior: 'smooth',
  });
}

const article = computed(() => {
  return props.article;
});

const { writerUrls, paginatedContents, contentsWithPage } = useTransformArticle(
  article.value,
);

const isNews = computed(() => {
  return props.article.folder_id === 100;
});

const isDietqa = computed(() => {
  return props.article.folder_id === 300;
});

function sanitizeHtml(content: string) {
  const { sanitizedHtml } = useSanitizeHtml(ref(content));
  return sanitizedHtml.value;
}

function changePage(page: number) {
  emit('changePage', page);
}

function clickedToc(page: number, pageSection: number) {
  emit('clickedToc', {
    page,
    pageSection,
  });
}

defineExpose({
  scrollToContents,
  contentAnchorRefs,
});
</script>

<template>
  <div>
    <div class="mb-10">
      <!-- NOTE: `article__title` はTaxelの表示のために必要 -->
      <h1 class="text-3xl font-bold inline article__title">
        {{ article.title }}
      </h1>
      <span v-if="isPr">【PR】</span>
    </div>

    <div v-if="isDietqa && categories.length" class="mb-5">
      <span v-for="(category, i) in categories" :key="category.category_id">
        {{ category.category_name }}
        <span v-if="i !== categories.length - 1" class="px-2">/</span>
      </span>
    </div>

    <div
      v-if="currentPage === 1"
      class="bg-gray-100 p-5 border border-gray-200 mb-3 w-full"
    >
      <button
        v-for="(content, i) in contentsWithPage"
        :key="i"
        class="not-last:mb-3 text-left flex"
        data-testid="toc"
        @click="clickedToc(content.page, content.pageSection)"
      >
        <span class="mr-2 shrink-0">{{ i + 1 }}.</span>
        <span class="underline">{{ content.title }}</span>
      </button>
    </div>

    <button
      v-if="article.writer"
      class="bg-gray-100 p-2 border border-gray-200 mb-5 w-full text-left"
      data-testid="scroll-to-writer-button"
      @click="scrollToWriter"
    >
      <span class="bg-orange-400 mr-2 text-white p-1">著者</span>
      <span class="font-bold mr-1">{{ article.writer.name }}</span>
      <span v-if="article.writer.title" class="text-sm text-gray-400"
        >({{ article.writer.title }})</span
      >
    </button>

    <div v-if="currentPage === 1">
      <img
        v-if="article.image_file"
        :src="createArticleImageUrl({ path: article.image_file })"
        :alt="article.title"
        class="mb-5"
      />

      <p v-if="article.lead" class="border border-gray-400 p-4 mb-5">
        {{ article.lead }}
      </p>
    </div>

    <slot name="before-contents" />

    <p class="text-right text-xs text-gray-400 mb-5">
      {{ formatPublicTs(article.public_ts!) }}
    </p>

    <slot name="share">
      <ShareButtonGroup />
    </slot>

    <div ref="contentsRef" data-testid="contentsRef">
      <div
        v-for="(content, i) in paginatedContents[currentPage - 1]"
        :key="i"
        ref="contentAnchorRefs"
      >
        <HeadingLv2 v-if="content.title">{{ content.title }}</HeadingLv2>

        <div v-for="(section, i2) in content.data.sections" :key="i2">
          <HeadingLv3 v-if="section.t">{{ section.t }}</HeadingLv3>
          <img
            v-if="section.i"
            :src="createArticleImageUrl({ path: section.i })"
            :class="{
              'mb-10': true,
              'w-full': section.f,
            }"
          />
          <!-- eslint-disable vue/no-v-html -->
          <p
            class="section-content mb-10 whitespace-pre-wrap leading-8"
            v-html="sanitizeHtml(section.c)"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
      </div>
    </div>

    <HeadingLv2>
      【やせ習慣が身につく】管理栄養士が食生活をコーディネートするアプリって？
    </HeadingLv2>
    <HeadingLv3>
      まずは無料でスタート♪食事を撮るだけ、プロから食事のアドバイスが届く！
    </HeadingLv3>
    <ul class="mb-7">
      <li class="list-disc list-inside pl-2 mb-2">
        専属の管理栄養士がダイエットをサポート
      </li>
      <li class="list-disc list-inside pl-2 mb-2">
        食制限なし！正しく食べて身につく「やせ習慣」♪
      </li>
      <li class="list-disc list-inside pl-2 mb-2">
        管理栄養士が、写真を目で見て丁寧にアドバイス。AIではありません！
      </li>
      <li class="list-disc list-inside pl-2 mb-2">
        「あってるかな？」そんな食事のお悩みを正しい知識でアドバイス
      </li>
    </ul>
    <div class="text-center mb-10">
      <NuxtLink
        class="app-button text-white py-4 px-8 rounded-md border border-red-400 font-bold inline-block"
        target="_blank"
        rel="noopener"
        to="/s/service_dietplus.html"
      >
        ダイエットアプリについて詳しく見る
      </NuxtLink>
    </div>

    <!-- NOTE: `article__category` はTaxelの表示のために必要 -->
    <div
      v-if="isNews && categories.length"
      class="text-sm mb-10 article__category"
    >
      <span>関連カテゴリ：</span>
      <NuxtLink
        v-for="category in categories"
        :key="category.category_id"
        :to="{
          path: '/public/article/news',
          query: {
            ct: category.category_id,
          },
        }"
        class="underline mr-4"
      >
        {{ category.category_name }}
      </NuxtLink>
    </div>

    <slot name="after-contents" />

    <FacebookFollowButton class="my-10" />

    <Pagination
      class="mb-10"
      :total-pages="paginatedContents.length"
      :current-page="currentPage"
      @change-page="changePage"
    />

    <div v-if="article.writer" ref="writerRef" data-testid="writerRef">
      <HeadingLv3> 著者 </HeadingLv3>
      <HeadingLv4>
        <span class="mr-2">{{ article.writer.name }}</span>
        <span v-if="article.writer.title">({{ article.writer.title }})</span>
      </HeadingLv4>

      <div class="flex">
        <div v-if="article.writer.image_file" class="mr-5 w-40 shrink-0">
          <img
            :src="createArticleImageUrl({ path: article.writer.image_file })"
          />
        </div>
        <div>
          <p class="mb-5">{{ article.writer.profile }}</p>
          <ul>
            <li v-for="(writerUrl, i) in writerUrls" :key="i">
              <NuxtLink
                :to="writerUrl.u"
                class="underline"
                target="_blank"
                rel="noopener"
              >
                {{ writerUrl.l }}
              </NuxtLink>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <hr class="h-px my-10 bg-gray-400 border-0" />

    <RelationKeywords v-if="isNews && article.tags" :tags="article.tags" />
    <slot name="footer" />
  </div>
</template>

<style scoped>
.app-button {
  background: linear-gradient(0deg, #ff5c77 0%, #ff8ca0 100%);
  box-shadow: 0px 0px 4px 0px #ffc7d0 inset;
}

:deep(.section-content a) {
  text-decoration: underline;
}
</style>
